'use client';

import Link from 'next/link';

import { Footer, Heading } from 'components/atomic/atoms';
import { HeaderOrganism } from 'components/atomic/organism';
import config from 'config/index';
import { getPageURL, ROUTE_CONFIG } from 'services/hostPortal.service';

const termsAndConditionPageURL = getPageURL({
  pageName: ROUTE_CONFIG.TERMS_AND_CONDITION.label
});

export default function Privacy() {
  return (
    <>
      <HeaderOrganism heading='Privacy Policy' />
      <main>
        <div className='policy-content'>
          <div className='bg-lets-go-btn max-h-35 md:max-h-64 h-full min-h-35 md:min-h-64'>
            <div className='text-center pt-7 md:pt-12'>
              <Heading
                level={2}
                className='text-lg md:text-4xl leading-5 md:leading-8 font-Montserrat font-medium'
              >
                WEBSITE PRIVACY POLICY
              </Heading>
            </div>
          </div>

          <div className='max-w-xs md:max-w-5xl w-full bg-white mx-auto rounded-xl transform -translate-y-16 md:-translate-y-32 shadow-profile'>
            <div className='p-5 md:p-10 text-xs md:text-sm font-normal text-nero leading-4 md:leading-7 pb-2.5'>
              <Heading
                level={3}
                className='font-Montserrat text-base md:text-xl leading-6 font-medium text-nero pb-2.5'
              >
                {' '}
                Introduction{' '}
              </Heading>
              <p> Welcome to Evinops Limited’s privacy policy. </p>

              <p>
                {' '}
                Evinops Limited respects your privacy and is committed to
                protecting your personal data. This privacy policy will inform
                you as to how we look after your personal data when you visit
                our website (regardless of where you visit it from) and tell you
                about your privacy rights and how the law protects you.{' '}
              </p>

              <p>
                {' '}
                Please use the Glossary to understand the meaning of some of the
                terms used in this privacy policy.{' '}
              </p>

              <Heading
                level={3}
                className='font-Montserrat text-base md:text-xl leading-6 font-medium text-nero mt-4 md:mt-8 pb-2.5'
              >
                {' '}
                1.Important information and who we are{' '}
              </Heading>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                Purpose of this Privacy Policy{' '}
              </span>

              <p>
                {' '}
                This privacy policy aims to give you information on how Evinops
                Limited collects and processes your personal data through your
                use of this website, including any data you may provide through
                this website when you sign up to our newsletter, purchase a
                product or service, or take part in a competition.{' '}
              </p>

              <p>
                {' '}
                This website is not intended for children and we do not
                knowingly collect data relating to children.
              </p>

              <p>
                {' '}
                It is important that you read this privacy policy together with
                any other privacy policy or fair processing policy we may
                provide on specific occasions when we are collecting or
                processing personal data about you so that you are fully aware
                of how and why we are using your data. This privacy policy
                supplements other notices and privacy policies and is not
                intended to override them.{' '}
              </p>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                Controller{' '}
              </span>

              <p>
                {' '}
                This privacy policy is issued on behalf of all companies in the
                Evinops Limited Group from time to time so when we mention
                Evinops Limited, <strong>we, us </strong> or{' '}
                <strong>our </strong> in this privacy policy, we are referring
                to the relevant company in the Evinops Limited Group responsible
                for processing your data. Evinops Limited is the controller and
                responsible for this website.{' '}
              </p>

              <p>
                {' '}
                We have appointed a data privacy manager who is responsible for
                overseeing questions in relation to this privacy policy. If you
                have any questions about this privacy policy, including any
                requests to exercise your legal rights, please contact the data
                privacy manager using the details set out below.{' '}
              </p>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                Contact details{' '}
              </span>
              <p>
                {' '}
                If you have any questions about this privacy policy or our
                privacy practices, please contact our data privacy manager in
                the following ways:{' '}
              </p>

              <div className='flex'>
                <div className='md:w-3/12'>
                  {' '}
                  <strong> Full name of legal entity: </strong>{' '}
                </div>
                <div className='md:w-9/12'> Evinops Limited </div>
              </div>

              <div className='flex'>
                <div className='w-3/12'>
                  {' '}
                  <strong> Email address: </strong>{' '}
                </div>
                <div className='w-9/12'>
                  {' '}
                  <a href={`mailto:${config.haflaEmail.contactUs}`}>
                    {config.haflaEmail.contactUs}.
                  </a>{' '}
                </div>
              </div>

              <div className='flex pb-2.5'>
                <div className='w-3/12'>
                  {' '}
                  <strong> Postal address: </strong>{' '}
                </div>
                <div className='w-9/12'>
                  {' '}
                  DD-14-124-053, 14 Floor, WeWork Hub71, Al Khatem Tower, ADGM
                  Square, Al Maryah Island, Abu Dhabi, UAE, Abu Dhabi, 46617,
                  United Arab Emirates{' '}
                </div>
              </div>

              <p>
                {' '}
                If you are concerned about our handling of your personal
                information, you can lodge a complaint with a privacy
                supervisory authority. In the European Economic Area, the
                relevant supervisory authority is the one in the country or
                territory where:{' '}
              </p>

              <ul className='list-alpha list-inside ps-4 md:ps-5 my-2.5 space-y-1'>
                <li> you are resident; </li>
                <li> you work, or </li>
                <li> the alleged infringement took place. </li>
              </ul>

              <p>
                {' '}
                A list of National Data Protection Authorities in the European
                Economic Area can be found here:
                <Link
                  href='http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm'
                  className='text-blue font-medium underline'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {' '}
                  http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm
                </Link>
                .
              </p>

              <p>
                {' '}
                We would appreciate the chance to deal with your concerns before
                you approach the relevant privacy supervisory authority, so
                please contact us in the first instance.{' '}
              </p>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                Changes to the Privacy Policy and your duty to inform us of
                changes{' '}
              </span>
              <p>
                {' '}
                We keep our privacy policy under regular review. This version
                was last reviewed and updated on 07 February 2022.{' '}
              </p>

              <p>
                {' '}
                It is important that the personal data we hold about you is
                accurate and current. Please keep us informed if your personal
                data changes during your relationship with us.{' '}
              </p>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                Third-party links{' '}
              </span>
              <p>
                {' '}
                This website may include links to third-party websites, plug-ins
                and applications. Clicking on those links or enabling those
                connections may allow third parties to collect or share data
                about you. We do not control these third-party websites and are
                not responsible for their privacy statements. When you leave our
                website, we encourage you to read the privacy policy of every
                website you visit.{' '}
              </p>

              <Heading
                level={3}
                className='font-Montserrat text-base md:text-xl leading-6 font-medium text-nero mt-4 md:mt-8 pb-2.5'
              >
                {' '}
                2. The data we collect about you{' '}
              </Heading>
              <p className='block'>
                {' '}
                Personal data or personal information means any information
                about an individual from which that person can be identified
                (either directly or indirectly). It does not include data where
                the identity has been removed (anonymous data).{' '}
              </p>

              <p className='block'>
                {' '}
                We may collect, use, store and transfer different kinds of
                personal data about you which we have grouped together as
                follows:{' '}
              </p>

              <ul className='list-alpha list-inside ps-4 md:ps-5 my-2.5 space-y-1'>
                <li>
                  {' '}
                  <strong> Identity Data </strong> includes first name, maiden
                  name, last name, username or similar identifier, marital
                  status, title, date of birth and gender;{' '}
                </li>
                <li>
                  {' '}
                  <strong> Contact Data </strong> includes billing address,
                  delivery address, email address and telephone numbers;{' '}
                </li>
                <li>
                  {' '}
                  <strong> Financial Data </strong> includes bank account and
                  payment card details;{' '}
                </li>
                <li>
                  {' '}
                  <strong> Transaction Data </strong> includes details about
                  payments to and from you and other details of products and
                  services you have purchased from us;{' '}
                </li>
                <li>
                  {' '}
                  <strong> Technical Data </strong> includes internet protocol
                  (IP) address, your login data, browser type and version, time
                  zone setting and location, browser plug-in types and versions,
                  operating system and platform, and other technology on the
                  devices you use to access this website;{' '}
                </li>
                <li>
                  {' '}
                  <strong> Profile Data </strong> includes your username and
                  password, purchases or orders made by you, your interests,
                  preferences, feedback and survey responses;{' '}
                </li>
                <li>
                  {' '}
                  <strong> Usage Data </strong> includes information about how
                  you use our website, products and services; and
                </li>
                <li>
                  {' '}
                  <strong> Marketing and Communications Data </strong> includes
                  your preferences in receiving marketing from us and our third
                  parties and your communication preferences.{' '}
                </li>
              </ul>

              <p>
                {' '}
                We also collect, use and share{' '}
                <strong> Aggregated Data </strong> such as statistical or
                demographic data for any purpose. Aggregated Data could be
                derived from your personal data but is not considered personal
                data in law as this data will not directly or indirectly reveal
                your identity. For example, we may aggregate your Usage Data to
                calculate the percentage of users accessing a specific website
                feature. However, if we combine or connect Aggregated Data with
                your personal data so that it can directly or indirectly
                identify you, we treat the combined data as personal data which
                will be used in accordance with this privacy policy.
              </p>

              <p>
                {' '}
                We do not collect any{' '}
                <strong> Special Categories of Personal Data</strong> about you
                (this includes details about your race or ethnicity, religious
                or philosophical beliefs, sex life, sexual orientation,
                political opinions, trade union membership, information about
                your health, and genetic and biometric data). Nor do we collect
                any information about criminal convictions and offences.
              </p>

              <p>
                {' '}
                <strong> If you fail to provide Personal Data </strong> Where we
                need to collect personal data by law, or under the terms of a
                contract we have with you, and you fail to provide that data
                when requested, we may not be able to perform the contract we
                have or are trying to enter into with you (for example, to
                provide you with goods or services). In this case, we may have
                to cancel a product or service you have with us, but we will
                notify you if this is the case at the time.{' '}
              </p>

              <Heading
                level={3}
                className='font-Montserrat text-base md:text-xl leading-6 font-medium text-nero mt-4 md:mt-8 pb-2.5'
              >
                {' '}
                3.How is your personal data collected?{' '}
              </Heading>
              <p>
                {' '}
                We use different methods to collect data from and about you
                including through:{' '}
              </p>

              <ul className='list-alpha list-inside ps-4 md:ps-5 my-2.5 space-y-1'>
                <li>
                  {' '}
                  <strong> Direct interactions. </strong> You may give us your
                  personal data by filling in forms or by corresponding with us
                  by post, phone, email or otherwise. This includes personal
                  data you provide when you:
                  <ul className='list-roman list-inside ps-4 md:ps-5 my-2.5 space-y-1'>
                    <li> apply for our products or services; </li>
                    <li> create an account on our website; </li>
                    <li> subscribe to our service or publications; </li>
                    <li> request marketing to be sent to you; </li>
                    <li> enter a competition, promotion or survey; or </li>
                    <li> give us feedback or contact us. </li>
                  </ul>
                </li>

                <li>
                  {' '}
                  <strong> Automated technologies or interactions. </strong> As
                  you interact with our website, we will automatically collect
                  Technical Data about your equipment, browsing actions and
                  patterns. We collect this personal data by using cookies,
                  server logs and other similar technologies. We may also
                  receive Technical Data about you if you visit other websites
                  employing our cookies. Please see our Cookie Notice
                  <Link
                    href={termsAndConditionPageURL}
                    className='text-blue font-medium'
                  >
                    {' '}
                    www.hafla.com/terms-and-conditions{' '}
                  </Link>
                  for further details.
                </li>

                <li>
                  {' '}
                  <strong>
                    {' '}
                    Third parties or publicly available sources.{' '}
                  </strong>{' '}
                  We will receive personal data about you from various third
                  parties and public sources as set out below.
                </li>

                <li>
                  {' '}
                  <strong> Technical Data </strong> from the following parties:
                  <ul className='list-roman list-inside ps-4 md:ps-5 my-2.5 space-y-1'>
                    <li>
                      {' '}
                      analytics providers such as Google based outside the
                      EU;{' '}
                    </li>
                    <li>
                      {' '}
                      advertising networks based inside and outside the EU;
                      and{' '}
                    </li>
                    <li>
                      {' '}
                      search information providers based inside and outside the
                      EU.{' '}
                    </li>
                  </ul>
                </li>

                <li>
                  {' '}
                  <strong>
                    {' '}
                    Contact, Financial and Transaction Data{' '}
                  </strong>{' '}
                  from providers of technical, payment and delivery services
                  based inside and outside the EU.
                </li>
                <li>
                  {' '}
                  Identity and Contact Data from data brokers or aggregators
                  based inside and outside the EU.
                </li>
                <li>
                  {' '}
                  Identity and Contact Data from publicly available sources such
                  as the Companies House and the Electoral Register based inside
                  the EU.
                </li>
              </ul>

              <Heading
                level={3}
                className='font-Montserrat text-base md:text-xl leading-6 font-medium text-nero mt-4 md:mt-8 pb-2.5'
              >
                {' '}
                4.How we use your Personal Data{' '}
              </Heading>
              <p>
                {' '}
                We will only use your personal data when the law allows us to.
                Most commonly, we will use your personal data where:
              </p>

              <ul className='list-alpha list-inside ps-4 md:ps-5 my-2.5 space-y-1'>
                <li>
                  {' '}
                  we need to perform the contract we are about to enter into or
                  have entered into with you;{' '}
                </li>

                <li>
                  {' '}
                  it is necessary for our Legitimate Interests (or those of a
                  third party) and your interests and fundamental rights do not
                  override those interests, or{' '}
                </li>

                <li> we need to comply with a legal obligation. </li>
              </ul>

              <p>
                {' '}
                Generally, we do not rely on consent as a legal basis for
                processing your personal data although we will get your consent
                before sending third party direct marketing communications to
                you via email or text message. You have the right to withdraw
                consent to marketing at any time by contacting us.
              </p>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                Purposes for which we will use your Personal Data{' '}
              </span>

              <p>
                {' '}
                We have set out below, in a table format, a description of all
                the ways we plan to use your personal data and which of the
                legal bases we rely on to do so. We have also identified what
                our Legitimate Interests are where appropriate.{' '}
              </p>

              <p>
                {' '}
                Note that we may process your personal data for more than one
                lawful ground depending on the specific purpose for which we are
                using your data. Please contact us if you need details about the
                specific legal ground we are relying on to process your personal
                data where more than one ground has been set out in the table
                below.
              </p>

              <table className='border text-left max-w-5xl m-auto w-full block my-5 overflow-x-scroll md:overflow-x-hidden'>
                <thead
                  className='w-full'
                  scope='col'
                >
                  <tr className='flex w-full'>
                    <th
                      scope='col'
                      className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-r border-b bg-platinum'
                    >
                      {' '}
                      Purpose/Activity{' '}
                    </th>
                    <th
                      scope='col'
                      className='min-w-7/10 md:min-w-auto w-7/10 md:w-1/5 px-2 py-1 border-r border-b bg-platinum'
                    >
                      {' '}
                      Type of data{' '}
                    </th>
                    <th
                      scope='col'
                      className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-b bg-platinum'
                    >
                      {' '}
                      Lawful basis for processing including basis of Legitimate
                      Interest{' '}
                    </th>
                  </tr>
                </thead>

                <tbody className=''>
                  <tr className='w-full flex'>
                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-r border-b'>
                      To register you as a new customer
                    </td>

                    <td className='min-w-7/10 md:min-w-auto w-7/10 md:w-1/5 px-2 py-1 border-r border-b'>
                      <ul className='list-alpha list-inside ps-2 md:ps-3 space-y-1'>
                        <li> Identity </li>
                        <li> Contact </li>
                      </ul>
                    </td>

                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-b'>
                      Performance of a contract with you{' '}
                    </td>
                  </tr>

                  <tr className='bg-white flex'>
                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-r border-b'>
                      To process and deliver your order including:
                      <ul className='list-alpha list-inside ps-2 md:ps-3 space-y-1'>
                        <li> Manage payments, fees and charges </li>
                        <li> Collect and recover money owed to us</li>
                      </ul>
                    </td>

                    <td className='min-w-7/10 md:min-w-auto w-7/10 md:w-1/5 px-2 py-1 border-r border-b'>
                      <ul className='list-alpha list-inside ps-2 md:ps-3 space-y-1'>
                        <li> Identity </li>
                        <li> Contact </li>
                        <li> Financial </li>
                        <li> Transaction </li>
                        <li> Marketing and Communications </li>
                      </ul>
                    </td>

                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-b'>
                      <ul className='list-alpha list-inside ps-2 md:ps-3 space-y-1'>
                        <li> Performance of a contract with you </li>
                        <li>
                          {' '}
                          Necessary for our Legitimate Interests (to recover
                          debts due to us){' '}
                        </li>
                      </ul>
                    </td>
                  </tr>

                  <tr className='bg-white flex'>
                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-r border-b'>
                      To manage our relationship with you which will include:
                      <ul className='list-alpha list-inside ps-2 md:ps-3 space-y-1'>
                        <li>
                          {' '}
                          Notifying you about changes to our terms or privacy
                          policy{' '}
                        </li>
                        <li> Asking you to leave a review or take a survey </li>
                      </ul>
                    </td>

                    <td className='min-w-7/10 md:min-w-auto w-7/10 md:w-1/5 px-2 py-1 border-r border-b'>
                      <ul className='list-alpha list-inside ps-2 md:ps-3 space-y-1'>
                        <li> Identity </li>
                        <li> Contact </li>
                        <li> Profile </li>
                        <li> Marketing and Communications </li>
                      </ul>
                    </td>

                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-b'>
                      <ul className='list-alpha list-inside ps-2 md:ps-3 space-y-1'>
                        <li> Performance of a contract with you </li>
                        <li> Necessary to comply with a legal obligation </li>
                        <li>
                          {' '}
                          Necessary for our Legitimate Interests (to keep our
                          records updated and to study how customers use our
                          products/services){' '}
                        </li>
                      </ul>
                    </td>
                  </tr>

                  <tr className='flex'>
                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-r border-b'>
                      To enable you to participate in a prize draw, competition
                      or complete a survey
                    </td>

                    <td className='min-w-7/10 md:min-w-auto w-7/10 md:w-1/5 px-2 py-1 border-r border-b'>
                      <ul className='list-alpha list-inside ps-2 md:ps-3 space-y-1'>
                        <li> Identity </li>
                        <li> Contact </li>
                        <li> Profile </li>
                        <li> Usage </li>
                        <li> Marketing and Communications </li>
                      </ul>
                    </td>

                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-b'>
                      <ul className='list-alpha list-inside ps-2 md:ps-3 space-y-1'>
                        <li> Performance of a contract with you </li>
                        <li>
                          {' '}
                          Necessary for our Legitimate Interests (to study how
                          customers use our products/services, to develop them
                          and grow our business){' '}
                        </li>
                      </ul>
                    </td>
                  </tr>

                  <tr className='bg-white flex'>
                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-r border-b'>
                      To administer and protect our business and this website
                      (including troubleshooting, data analysis, testing, system
                      maintenance, support, reporting and hosting of data)
                    </td>

                    <td className='min-w-7/10 md:min-w-auto w-7/10 md:w-1/5 px-2 py-1 border-r border-b'>
                      <ul className='list-alpha list-inside ps-4 md:ps-5 my-2.5 space-y-1'>
                        <li> Identity </li>
                        <li> Contact </li>
                        <li> Technical </li>
                      </ul>
                    </td>

                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-b'>
                      <ul className='list-alpha list-inside ps-2 md:ps-3 space-y-1'>
                        <li>
                          {' '}
                          Necessary for our Legitimate Interests (for running
                          our business, provision of administration and IT
                          services, network security, to prevent fraud and in
                          the context of a business reorganisation or group
                          restructuring exercise){' '}
                        </li>
                        <li> Necessary to comply with a legal obligation </li>
                      </ul>
                    </td>
                  </tr>

                  <tr className='bg-white flex'>
                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-r border-b'>
                      To deliver relevant website content and advertisements to
                      you and measure or understand the effectiveness of the
                      advertising we serve to you
                    </td>

                    <td className='min-w-7/10 md:min-w-auto w-7/10 md:w-1/5 px-2 py-1 border-r border-b'>
                      <ul className='list-alpha list-inside ps-2 md:ps-3 space-y-1'>
                        <li> Identity </li>
                        <li> Contact </li>
                        <li> Profile </li>
                        <li> Usage </li>
                        <li> Marketing and Communications </li>
                        <li> Technical </li>
                      </ul>
                    </td>

                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-b'>
                      Necessary for our Legitimate Interests (to study how
                      customers use our products/services, to develop them, to
                      grow our business and to inform our marketing
                      strategy){' '}
                    </td>
                  </tr>

                  <tr className='bg-white flex'>
                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-r border-b'>
                      To use data analytics to improve our website,
                      products/services, marketing, customer relationships and
                      experiences
                    </td>

                    <td className='min-w-7/10 md:min-w-auto w-7/10 md:w-1/5 px-2 py-1 border-r border-b'>
                      <ul className='list-alpha list-inside ps-2 md:ps-3 space-y-1'>
                        <li> Technical </li>
                        <li> Usage </li>
                      </ul>
                    </td>

                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-b'>
                      Necessary for our Legitimate Interests (to define types of
                      customers for our products and services, to keep our
                      website updated and relevant, to develop our business and
                      to inform our marketing strategy)
                    </td>
                  </tr>

                  <tr className='bg-white flex'>
                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1 border-r'>
                      To make suggestions and recommendations to you about goods
                      or services that may be of interest to you
                    </td>

                    <td className='min-w-7/10 md:min-w-auto w-7/10 md:w-1/5 px-2 py-1 border-r'>
                      <ul className='list-alpha list-inside ps-2 md:ps-3 space-y-1'>
                        <li> Identity </li>
                        <li> Contact </li>
                        <li> Technical </li>
                        <li> Usage </li>
                        <li> Profile </li>
                        <li> Marketing and Communications </li>
                      </ul>
                    </td>

                    <td className='min-w-full md:min-w-auto w-full md:w-2/5 px-2 py-1'>
                      Necessary for our Legitimate Interests (to develop our
                      products/services and grow our business)
                    </td>
                  </tr>
                </tbody>
              </table>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                Marketing{' '}
              </span>
              <p>
                {' '}
                We strive to provide you with choices regarding certain personal
                data uses, particularly around marketing and advertising.{' '}
              </p>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                Promotional offers from us{' '}
              </span>
              <p>
                {' '}
                We may use your Identity, Contact, Technical, Usage and Profile
                Data to form a view on what we think you may want or need, or
                what may be of interest to you. This is how we decide which
                products, services and offers may be relevant for you (we call
                this marketing). You will receive marketing communications from
                us if you have requested information from us or purchased from
                us and you have not opted out of receiving that marketing.
              </p>

              <span className='font-semibold text-nero py-2.5 block'>
                Third-party marketing{' '}
              </span>
              <p>
                {' '}
                We will get your express opt-in consent before we share your
                personal data with any third party for marketing purposes.
              </p>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                Opting out{' '}
              </span>
              <p>
                {' '}
                You can ask us or third parties to stop sending you marketing
                messages at any time by contacting us at any time.{' '}
              </p>

              <p>
                {' '}
                Where you opt-out of receiving these marketing messages, this
                will not apply to personal data provided to us as a result of a
                product/service purchase, warranty registration, product/service
                experience or other transactions.{' '}
              </p>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                Cookies{' '}
              </span>
              <p>
                {' '}
                A cookie is a small text file that is placed onto your device
                when you access our website. We use cookies and other online
                tracking devices to make your online experience more efficient
                and enjoyable. You can set your browser to refuse all or some
                browser cookies or to alert you when websites set or access
                cookies. If you disable or refuse cookies, please note that some
                parts of this website may become inaccessible or not function
                properly. For more information about the cookies we use, please
                see our Cookie Notice
                <Link
                  href={termsAndConditionPageURL}
                  className='text-blue font-medium'
                >
                  {' '}
                  www.hafla.com/terms-and-conditions
                </Link>
                .
              </p>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                Change of purpose{' '}
              </span>
              <p>
                {' '}
                We will only use your personal data for the purposes for which
                we collected it, unless we reasonably consider that we need to
                use it for another reason and that reason is compatible with the
                original purpose. If you wish to get an explanation as to how
                the processing for the new purpose is compatible with the
                original purpose, please contact us.{' '}
              </p>

              <p>
                {' '}
                If we need to use your personal data for an unrelated purpose,
                we will notify you and we will explain the legal basis which
                allows us to do so.{' '}
              </p>

              <p>
                {' '}
                Please note that we may process your personal data without your
                knowledge or consent, in compliance with the above rules, where
                this is required or permitted by law.{' '}
              </p>

              <Heading
                level={3}
                className='font-Montserrat text-base md:text-xl leading-6 font-medium text-nero mt-4 md:mt-8 pb-2.5'
              >
                {' '}
                5.Disclosures of your Personal Data{' '}
              </Heading>

              <p>
                {' '}
                We may share your personal data with the parties set out below
                for the{' '}
                <strong>
                  {' '}
                  purposes set out in the table above in the Purposes for which
                  we will use your Personal Data section.{' '}
                </strong>{' '}
              </p>

              <ul className='list-alpha list-inside ps-4 md:ps-5 my-2.5 space-y-1'>
                <li>
                  {' '}
                  Internal Third Parties: companies in the Evinops Limited
                  Group;{' '}
                </li>

                <li>
                  {' '}
                  External Third Parties: including service providers and
                  professional advisors to Evinops Limited Group;{' '}
                </li>

                <li>
                  {' '}
                  third parties to whom we may choose to sell, transfer or merge
                  parts of our business or our assets. Alternatively, we may
                  seek to acquire other businesses or merge with them. If a
                  change happens to our business, then the new owners may use
                  your personal data in the same way as set out in this privacy
                  policy;{' '}
                </li>

                <li>
                  {' '}
                  any other third party if we are under a duty to disclose or
                  share your personal data to comply with any legal obligation,
                  or to protect the rights, property and or safety of Evinops
                  Limited, our personnel or others; and{' '}
                </li>

                <li>
                  {' '}
                  any other third party for the purposes of acting in accordance
                  with the requirements of a court, regulator or government
                  agency, for example complying with a search warrant or court
                  order or acting in accordance with applicable law or
                  regulation.{' '}
                </li>
              </ul>

              <p>
                {' '}
                We require all third parties to respect the security of your
                personal data and to treat it in accordance with the law. We do
                not allow our third-party service providers to use your personal
                data for their own purposes and only permit them to process your
                personal data for specified purposes and in accordance with our
                instructions.{' '}
              </p>

              <Heading
                level={3}
                className='font-Montserrat text-base md:text-xl leading-6 font-medium text-nero mt-4 md:mt-8 pb-2.5'
              >
                {' '}
                6.International Transfers{' '}
              </Heading>

              <p>
                {' '}
                We share your personal data within the Evinops Limited Group.
                This may involve transferring your data outside the European
                Economic Area <strong>(EEA)</strong>.{' '}
              </p>

              <p>
                {' '}
                Our external third parties may be based outside the EEA so their
                processing of your personal data may involve a transfer of data
                outside the EEA.{' '}
              </p>

              <p>
                {' '}
                Whenever we transfer your personal data out of the EEA, we
                ensure a similar degree of protection is afforded to it by
                ensuring at least one of the following safeguards is
                implemented:{' '}
              </p>

              <p>
                {' '}
                Please contact us if you want further information on the
                specific mechanism used by us when transferring your personal
                data out of the EEA.{' '}
              </p>

              <ul className='list-alpha list-inside ps-4 md:ps-5 my-2.5 space-y-1'>
                <li>
                  {' '}
                  we will only transfer your personal data to countries that
                  have been deemed to provide an adequate level of protection
                  for personal data by the European Commission. For further
                  details, see{' '}
                  <strong className='italic'>
                    {' '}
                    European Commission: Adequacy of the protection of personal
                    data in non-EU countries;{' '}
                  </strong>
                </li>

                <li>
                  {' '}
                  where we use certain service providers, we may use specific
                  contracts approved by the European Commission which give
                  personal data the same protection it has in Europe. For
                  further details, see{' '}
                  <strong className='italic'>
                    {' '}
                    European Commission: Model contracts for the transfer of
                    personal data to third countries;{' '}
                  </strong>
                  or
                </li>

                <li>
                  {' '}
                  where we use providers based in the US, we may transfer data
                  to them if they are part of the Privacy Shield which requires
                  them to provide similar protection to personal data shared
                  between Europe and the US. For further details, see{' '}
                  <strong className='italic'>
                    {' '}
                    European Commission: EU-US Privacy Shield.{' '}
                  </strong>
                </li>
              </ul>

              <p>
                {' '}
                Please contact us if you want further information on the
                specific mechanism used by us when transferring your personal
                data out of the EEA.{' '}
              </p>

              <Heading
                level={3}
                className='font-Montserrat text-base md:text-xl leading-6 font-medium text-nero mt-4 md:mt-8 pb-2.5'
              >
                {' '}
                7.Data Security{' '}
              </Heading>

              <p>
                {' '}
                We have put in place appropriate security measures to prevent
                your personal data from being accidentally lost, used or
                accessed in an unauthorized way, altered or disclosed. In
                addition, we limit access to your personal data to those
                employees, agents, contractors and other third parties who have
                a business need to know. They will only process your personal
                data on our instructions and they are subject to a duty of
                confidentiality.{' '}
              </p>

              <p>
                {' '}
                Unfortunately, no transmission of data over the internet is
                guaranteed to be completely secure. It may be possible for
                unauthorized third parties, to intercept or access transmissions
                or private communications unlawfully. While we strive to protect
                your personal data, we cannot ensure or warrant the security of
                any personal data you transmit to us. Any such transmission is
                done at your own risk. If you believe that your interaction with
                us is no longer secure, please contact us.{' '}
              </p>

              <p>
                {' '}
                We have put in place procedures to deal with any suspected
                personal data breach and will notify you and any applicable
                regulator of a breach where we are legally required to do
                so.{' '}
              </p>

              <Heading
                level={3}
                className='font-Montserrat text-base md:text-xl leading-6 font-medium text-nero mt-4 md:mt-8 pb-2.5'
              >
                {' '}
                8.Data Retention{' '}
              </Heading>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                How long will you use my personal data for?{' '}
              </span>

              <p>
                {' '}
                We will only retain your personal data for as long as reasonably
                necessary to fulfil the purposes we collected it for, including
                for the purposes of satisfying any legal, regulatory, tax,
                accounting or reporting requirements. We may retain your
                personal data for a longer period in the event of a complaint or
                if we reasonably believe there is a prospect of litigation in
                respect to our relationship with you.{' '}
              </p>

              <p>
                {' '}
                To determine the appropriate retention period for personal data,
                we consider the amount, nature and sensitivity of the personal
                data, the potential risk of harm from unauthorised use or
                disclosure of your personal data, the purposes for which we
                process your personal data and whether we can achieve those
                purposes through other means, and the applicable legal,
                regulatory, tax, accounting or other requirements.{' '}
              </p>

              <p>
                {' '}
                By law, we have to keep basic information about our customers
                (including Contact, Identity, Financial and Transaction Data)
                for six years after they cease being customers for tax
                purposes.{' '}
              </p>

              <p>
                {' '}
                In some circumstances you can ask us to delete your data: see{' '}
                <strong> Your Legal Rights </strong> in section 9 below for
                further information.
              </p>

              <p>
                {' '}
                In some circumstances, we will anonymise your personal data (so
                that it can no longer be associated with you) for research or
                statistical purposes, in which case we may use this information
                indefinitely without further notice to you.{' '}
              </p>

              <p>
                {' '}
                Whilst we continue to process your personal data we will ensure
                that it is treated in accordance with this privacy policy.
                Otherwise, we securely erase your information once it is no
                longer needed.{' '}
              </p>

              <Heading
                level={3}
                className='font-Montserrat text-base md:text-xl leading-6 font-medium text-nero mt-4 md:mt-8 pb-2.5'
              >
                {' '}
                9.Your Legal Rights{' '}
              </Heading>
              <p>
                {' '}
                Under certain circumstances, you have rights under data
                protection laws in relation to your personal data. The various
                rights are not absolute and each is subject to certain
                exceptions or qualifications. We will grant your request only to
                the extent that it follows from our assessment of your request
                that we are allowed and required to do so under data protection
                laws. Nothing in this privacy policy is intended to provide you
                with rights beyond or in addition to your rights as a data
                subject under data protection laws. You have the right to:{' '}
              </p>

              <ul className='list-alpha list-inside ps-4 md:ps-5 my-2.5 space-y-1'>
                <li>
                  {' '}
                  <strong> request access </strong> to your personal data
                  (commonly known as a “data subject access request”). This
                  enables you to receive a copy of the personal data we hold
                  about you and to check that we are lawfully processing
                  it;{' '}
                </li>

                <li>
                  {' '}
                  <strong> request correction </strong> of the personal data
                  that we hold about you. This enables you to have any
                  incomplete or inaccurate data we hold about you corrected,
                  though we may need to verify the accuracy of the new data you
                  provide to us;{' '}
                </li>

                <li>
                  {' '}
                  <strong> request erasure </strong> of your personal data. This
                  enables you to ask us to delete or remove personal data where
                  there is no good reason for us continuing to process it. You
                  also have the right to ask us to delete or remove your
                  personal data where you have successfully exercised your right
                  to object to processing (see below), where we may have
                  processed your information unlawfully or where we are required
                  to erase your personal data to comply with local law. Note
                  that we may not always be able to comply with your request of
                  erasure for specific legal reasons which will be notified to
                  you, if applicable, at the time of your request;{' '}
                </li>

                <li>
                  {' '}
                  <strong> object to processing </strong> of your personal data
                  where we are relying on a Legitimate Interest (or those of a
                  third party) and there is something about your particular
                  situation which makes you want to object to processing on this
                  ground as you feel it impacts on your fundamental rights and
                  freedoms. You also have the right to object where we are
                  processing your personal data for direct marketing purposes.
                  In some cases, we may demonstrate that we have compelling
                  legitimate grounds to process your information which override
                  your rights and freedoms;{' '}
                </li>

                <li>
                  {' '}
                  <strong> request restriction of processing </strong> of your
                  personal data. This enables you to ask us to suspend the
                  processing of your personal data in the following
                  scenarios:{' '}
                </li>

                <ul className='list-alpha list-inside ps-4 md:ps-5 my-2.5 space-y-1'>
                  <li> where you want us to establish the data’s accuracy; </li>
                  <li>
                    {' '}
                    where our use of the data is unlawful but you do not want us
                    to erase it;{' '}
                  </li>
                  <li>
                    {' '}
                    where you need us to hold the data even if we no longer
                    require it as you need it to establish, exercise or defend
                    legal claims; or{' '}
                  </li>
                  <li>
                    {' '}
                    you have objected to our use of your data but we need to
                    verify whether we have overriding legitimate grounds to use
                    it;{' '}
                  </li>
                </ul>

                <li>
                  {' '}
                  <strong> request the transfer </strong> of your personal data
                  to you or to a third party. We will provide to you, or a third
                  party you have chosen, your personal data in a structured,
                  commonly used, machine-readable format. Note that this right
                  only applies to automated information which you initially
                  provided consent for us to use or where we used the
                  information to perform a contract with you; and{' '}
                </li>

                <li>
                  {' '}
                  <strong> withdraw consent at any time </strong> where we are
                  relying on consent to process your personal data. However,
                  this will not affect the lawfulness of any processing carried
                  out before you withdraw your consent. If you withdraw your
                  consent, we may not be able to provide certain products or
                  services to you. We will advise you if this is the case at the
                  time you withdraw your consent.{' '}
                </li>
              </ul>

              <p>
                {' '}
                If you wish to exercise any of the rights set out above, please
                contact us.{' '}
              </p>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                No fee usually required{' '}
              </span>

              <p>
                {' '}
                You will not ordinarily have to pay a fee to access your
                personal data (or to exercise any of the other rights). However,
                we may charge a reasonable fee if your request is clearly
                unfounded, repetitive or excessive. Alternatively, we could
                refuse to comply with your request in these circumstances.{' '}
              </p>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                What we may need from you{' '}
              </span>

              <p>
                {' '}
                We may need to request specific information from you to help us
                confirm your identity and ensure your right to access your
                personal data (or to exercise any of your other rights). This is
                a security measure to ensure that personal data is not disclosed
                to any person who has no right to receive it. We may also
                contact you to ask you for further information in relation to
                your request to speed up our response.{' '}
              </p>

              <span className='font-semibold text-nero py-2.5 block'>
                {' '}
                Time limit to respond{' '}
              </span>

              <p>
                {' '}
                We try to respond to all legitimate requests within one month.
                Occasionally it could take us longer if your request is
                particularly complex or you have made a number of requests. In
                this case, we will notify you and keep you updated.
              </p>

              <Heading
                level={3}
                className='font-Montserrat text-base md:text-xl leading-6 font-medium text-nero mt-4 md:mt-8 pb-2.5'
              >
                {' '}
                10.Glossary{' '}
              </Heading>

              <p className='block'>
                {' '}
                <strong> Legitimate Interest </strong> means the interest of our
                business in conducting and managing our business to enable us to
                give you the best service/product and the best and most secure
                experience. We make sure we consider and balance any potential
                impact on you (both positive and negative) and your rights
                before we process your personal data for our Legitimate
                Interests. We do not use your personal data for activities where
                our interests are overridden by the impact on you (unless we
                have your consent or are otherwise required or permitted to by
                law). You can obtain further information about how we assess our
                Legitimate Interests against any potential impact on you in
                respect of specific activities by contacting us.
              </p>

              <p className='block'>
                {' '}
                <strong> Performance of Contract </strong> means processing your
                data where it is necessary for the performance of a contract to
                which you are a party or to take steps at your request before
                entering into such a contract.
              </p>

              <p className='block'>
                {' '}
                <strong> Comply with a legal obligation </strong> means
                processing your personal data where it is necessary for
                compliance with a legal obligation that we are subject to.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
